<script setup lang="ts">
import { inject } from 'vue'

import ElementalListMenu from '@/components/ElemetalMenu/index.vue'
import { AccordionContextSymbol } from '@/components/Accordion/context'
import { useSidebarStore } from '~~/src/stores/sidebar'
import { useElementalListsStore } from '~~/src/stores/useElementalListsStore'
import type { List } from '~~/src/types/list'
import type { Elemental, ElementalTypeEnum } from '~~/src/types/elemental'

const props = defineProps<{
  isMain?: boolean
  title?: string
  nPrompts?: number
  iconBoxClass?: string
  listId: string
  parentListId?: string | number
  list?: List | Elemental
  elementalsType: ElementalTypeEnum
}>()
const { toggleElementOpen } = useSidebarStore()

const accordeonContext = inject(AccordionContextSymbol)

if (!accordeonContext)
  throw new Error('Accordeon context not found')

const { isOpen, toggle } = accordeonContext

watch(isOpen, (value) => {
  if (props.listId)
    toggleElementOpen(props.listId, props.parentListId ? `${props.parentListId}` : null, value)
})

const handlerToggle = () => {
  toggle()
}

const { state } = useElementalListsStore()
</script>

<template>
  <button
    v-if="!isMain"
    class="flex items-center w-full gap-1.5 text-gray-5 hover:bg-grayLighter  rounded-lg "
    :class="[$style.wrapper, { 'bg-grayLighter rounded-t-1.6 rounded-b-none': isOpen }]"
    @click="handlerToggle()"
  >
    <div class="flex gap-1.5 drag-image" :class="[$style['drag-view']]">
      <slot name="icon" />
      <p
        text-sm
        text-softBlack
        line-clamp-1
        text-start
        :class="{
          'font-bold': isOpen,
        }"
      >
        <UtilsHighLightText :text="title" :search="state.query" />
      </p>
    </div>

    <BubbleCount
      v-if="isMain"
    >
      {{ nPrompts }}
    </BubbleCount>

    <span flex items-center justify-self-end ml-a>

      <div :class="$style.actions">
        <nav
          v-if="!isMain"
          flex
          gap-1.5
          items-center
          bg-transparent
          px-1
          :class="$style.actions__hidden"
        >
          <ElementalListMenu v-if="list" :elemental="list" :elementals-type="elementalsType" is-list-element :parent-list-id="parentListId" />
        </nav>
        <span
          flex items-center justify-self-end ml-a mr-1 w-6
          h-6
        />
      </div>
      <div
        i-material-symbols:keyboard-arrow-down
        :rotate="isOpen ? -180 : 0"
        w-5
        h-5
        text-dark-gray
      />
    </span>
  </button>
  <button
    v-else
    class="flex items-center w-full gap-1.5 text-gray-5"
    :class="[$style.wrapper]"
    @click="handlerToggle()"
  >
    <div class="flex gap-1.5">
      <slot name="icon" />
      <p
        text-sm
        text-softBlack
        line-clamp-1
        text-start
        :class="{
          'font-bold': isOpen,
        }"
      >
        <UtilsHighLightText :text="title" :search="state.query" />
      </p>
    </div>

    <BubbleCount
      v-if="isMain"
    >
      {{ nPrompts }}
    </BubbleCount>

    <span flex items-center justify-self-end ml-a>

      <div :class="$style.actions">
        <nav
          v-if="!isMain"
          flex
          gap-1.5
          items-center
          bg-transparent
          px-1
          :class="$style.actions__hidden"
        >
          <ElementalListMenu v-if="list" :elemental="list" :elementals-type="elementalsType" is-list-element />
        </nav>
        <span
          flex items-center justify-self-end ml-a mr-1 w-6
          h-6
        />
      </div>
      <div
        i-material-symbols:keyboard-arrow-down
        :rotate="isOpen ? -180 : 0"
        w-5
        h-5
        text-dark-gray
      />
    </span>
  </button>
</template>

  <style module lang="css">
    .drag-view {
    &[data-dragged='true'] {
    @apply bg-white opacity-1 p-1.5 rounded-2; ;
    }
    }

    .wrapper {
    @apply w-auto
    w-full
    flex
    flex-nowrap
    justify-left
    py-2
    pl-2
    gap-2
    items-center
    relative;
    &:focus-within,
    &:hover {
    .actions {
    .actions__hidden {
    @apply flex;
    }
    }
    }

    .actions {
    @apply flex
    gap-.5
    justify-self-end
    ml-auto
    absolute
    right-0
    z-10
    items-center;
    }
    .actions__hidden {
    @apply hidden items-center;
    }
    }
  </style>
